import { useEffect, useState } from 'react'
import { Pencil, PauseCircle, PlayCircle, Archive, Radio, X } from 'lucide-react'
import { Modal, Select, adminMutate, apiGet, useToast } from '../admin/primitives.js'
import { Field, TextInput, NumberInput, SwitchRow } from '../admin/form.js'
import { useSaveBar } from '../shell/editing.js'
import { ConfirmDialog } from '../auth/Dialog.js'
import { MarketEventCard, ageFr } from './MarketEventCard.js'
import { MarketEventDrawer, type MarketEventDetail } from './MarketEventDrawer.js'
import { VenueAdvancedSections } from './VenueAdvancedSections.js'

/**
 * FICHE D'UN ÉTABLISSEMENT SURVEILLÉ — CONSULTATION D'ABORD.
 *
 * Cliquer sur un établissement ouvrait directement un formulaire d'édition
 * dans un tiroir de 480 px : on tombait dans vingt-quatre champs sans avoir
 * répondu à la seule question qu'on se pose en arrivant — « qu'est-ce qu'il
 * fait, et est-ce que ma veille le voit bien ? ».
 *
 * Cette surface est donc une FICHE DE MONITORING. L'édition est un MODE de
 * la même modale, pas une seconde fenêtre : une cascade
 * modale → tiroir → modale fait perdre le fil et le contexte.
 *
 * Grande modale centrale plutôt qu'un tiroir : il y a des synchronisations,
 * des soirées, des signaux et des actions à montrer. 480 px n'y suffisent
 * pas, et tasser du contenu dans un tiroir « parce qu'un tiroir existait »
 * n'est pas une raison de conception.
 */

export interface VenueDetail {
  venue: {
    id: string; name: string; city: string | null; website: string | null
    distance_km: number | null; status: string; enabled: boolean
    category: string; priority_level: string; competition_class: string; notes: string | null
    instagram_handle: string | null; custom_terms: string[]
  }
  branches: BranchView[]
  sources: {
    id: string; type: string; label: string | null; url: string | null
    platform: string | null; account_handle: string | null; enabled: boolean
    state: string; failure_count: number; last_error: string | null
    last_success_at: string | null; next_check_at: string | null
    instagram_stories_enabled?: boolean
  }[]
  queries: {
    id: string; query_template: string
    query_type: 'AUTO' | 'CUSTOM'; priority_level: string; enabled: boolean
  }[]
  coverage: Record<string, string>
}

export interface BranchView {
  key: 'WEB' | 'INSTAGRAM_CONTENT' | 'INSTAGRAM_STORIES'
  label: string
  enabled: boolean
  state: string
  last_attempt_at: string | null
  last_success_at: string | null
  next_check_at: string | null
  interval_s: number | null
  cadence: string
  last_error: string | null
  detail: string | null
}

interface Preset { key: string; label: string; seconds: number | null; hint: string }

/**
 * L'état d'une branche, dit sans jargon et sans dramatiser.
 *
 * Une branche éteinte n'est pas en panne (§65) ; « aucun résultat » n'est pas
 * « collecte impossible » (§64). Confondre les deux fait chercher une panne
 * là où il n'y a qu'un silence.
 */
const ETAT_BRANCHE: Record<string, { label: string; ton: 'ok' | 'warn' | 'bad' | 'off' }> = {
  HEALTHY: { label: 'Opérationnelle', ton: 'ok' },
  STALE: { label: 'À revérifier', ton: 'warn' },
  UNAVAILABLE: { label: 'Indisponible', ton: 'bad' },
  NOT_CONFIGURED: { label: 'Non configurée', ton: 'off' },
  UNKNOWN: { label: 'Jamais relevée', ton: 'off' },
  NONE: { label: 'Désactivée', ton: 'off' },
}

const CATEGORY_OPTIONS = [
  { value: 'NIGHTCLUB', label: 'Boîte de nuit' }, { value: 'CLUB', label: 'Club' },
  { value: 'BAR', label: 'Bar' }, { value: 'BAR_NIGHTLIFE', label: 'Bar nightlife' },
  { value: 'CONCERT_VENUE', label: 'Salle de concert' }, { value: 'CULTURAL_VENUE', label: 'Lieu culturel' },
  { value: 'STUDENT_VENUE', label: 'Lieu étudiant' }, { value: 'STUDENT_EVENT_ORGANIZER', label: 'Organisateur étudiant / BDE' },
  { value: 'PROMOTER', label: 'Organisateur' }, { value: 'FESTIVAL', label: 'Festival' },
  { value: 'OTHER', label: 'Autre' },
]
const PRIORITY_OPTIONS = [
  { value: 'HIGH', label: 'Haute (concurrent direct)' },
  { value: 'NORMAL', label: 'Normale' },
  { value: 'LOW', label: 'Basse (lieu occasionnel)' },
]
const CLASS_OPTIONS = [
  { value: 'DIRECT', label: 'Concurrent direct' },
  { value: 'ADJACENT', label: 'Nightlife adjacente' },
  { value: 'INDIRECT', label: 'Concurrence indirecte' },
  { value: 'CONTEXTUAL', label: 'Contextuel (salle / culture)' },
]
const COVERAGE_FR: Record<string, string> = {
  GOOD: 'bonne', STALE: 'à revérifier', UNAVAILABLE: 'indisponible',
  NOT_CONFIGURED: 'non configurée', NONE: 'aucune source', UNKNOWN: 'inconnue',
  PARTIAL: 'partielle (référence)',
}
const COVERAGE_LABEL: Record<string, string> = {
  WEBSITE: 'Site / agendas', TICKETING: 'Billetterie', WEB_SEARCH: 'Recherche web',
  FEEDS: 'Flux (RSS/ICS)', INSTAGRAM: 'Instagram', INSTAGRAM_STORIES: 'Stories Instagram',
}

/** « dans 1 h 53 » — une distance, jamais un horodatage brut. */
function dansFr(iso: string | null): string {
  if (!iso) return '—'
  const delta = new Date(iso).getTime() - Date.now()
  if (delta <= 0) return 'imminente'
  const min = Math.floor(delta / 60_000)
  if (min < 60) return `dans ${min} min`
  const h = Math.floor(min / 60)
  const reste = min % 60
  return reste > 0 ? `dans ${h} h ${String(reste).padStart(2, '0')}` : `dans ${h} h`
}

export function VenueDetailModal({ venueId, open, onClose, canManage, onChanged }: {
  venueId: string | null
  open: boolean
  onClose: () => void
  canManage: boolean
  onChanged: () => void
}) {
  const toast = useToast()
  const [d, setD] = useState<VenueDetail | null>(null)
  const [presets, setPresets] = useState<Preset[]>([])
  const [edition, setEdition] = useState(false)
  const [confirmArchive, setConfirmArchive] = useState(false)
  const [scanOpen, setScanOpen] = useState(false)

  // Snapshot d'ouverture : « Annuler » y revient, sans requête d'écriture.
  const [form, setForm] = useState({ category: '', priority_level: '', competition_class: '', instagram_handle: '' })

  const charger = async () => {
    if (!venueId) return
    const j = await apiGet<VenueDetail>(`/api/v1/soirees/market/venues/${venueId}`).catch(() => null)
    if (!j) return
    setD(j)
    setForm({
      category: j.venue.category, priority_level: j.venue.priority_level,
      competition_class: j.venue.competition_class,
      instagram_handle: j.venue.instagram_handle ?? '',
    })
  }
  useEffect(() => { if (open && venueId) { setEdition(false); void charger() } }, [open, venueId])
  useEffect(() => {
    void apiGet<{ presets: Preset[] }>('/api/v1/soirees/market/cadence-presets')
      .then((r) => setPresets(r.presets)).catch(() => undefined)
  }, [])

  const v = d?.venue
  // Les sections avancées attendent la forme brute du détail : on la leur
  // passe telle quelle plutôt que d'en maintenir une seconde.
  const detailBrut = d as unknown as Parameters<typeof VenueAdvancedSections>[0]['detail']
  const dirty = !!v && edition && (
    form.category !== v.category || form.priority_level !== v.priority_level
    || form.competition_class !== v.competition_class
    || form.instagram_handle !== (v.instagram_handle ?? '')
  )

  // §21 — la Floating Save Bar globale, jamais un bouton « Enregistrer » isolé.
  useSaveBar({
    dirty,
    saveLabel: 'Enregistrer l’établissement',
    onSave: async () => {
      if (!venueId) return 'Établissement inconnu.'
      /*
       * LA ROUTE COMPTE — ET CELLE-CI N'ÉCOUTAIT PAS.
       *
       * On écrivait vers `/venues/:id`, dont le schéma n'accepte que
       * `enabled`, `status`, `distance_km` et `sources`. Fastify RETIRE les
       * propriétés inconnues au lieu de les refuser : le corps arrivait vidé
       * de nos quatre champs, chaque `COALESCE(NULL, …)` gardait l'existant,
       * et la route répondait `{ok: true}`.
       *
       * L'écran voyait donc un succès, rechargeait, et réaffichait l'ancienne
       * valeur. Une écriture vide annoncée comme réussie — le pire des deux
       * mondes, parce que rien ne signalait l'échec.
       *
       * `/profile` est la route qui accepte réellement ces champs.
       */
      const r = await adminMutate('PATCH', `/api/v1/soirees/market/venues/${venueId}/profile`, {
        category: form.category, priority_level: form.priority_level,
        competition_class: form.competition_class,
        // Chaîne vide = « retirer le compte » ; la route la distingue de
        // « champ absent », qui ne toucherait à rien.
        instagram_handle: form.instagram_handle.trim(),
      })
      if (r.status !== 200) return 'Enregistrement impossible.'
      // Le rechargement redevient la référence : `charger()` remet `form` sur
      // ce que la base contient VRAIMENT. C'est le nouveau point de départ,
      // pas l'ancien instantané.
      await charger(); onChanged()
      return true
    },
    onCancel: () => {
      if (!v) return
      setForm({
        category: v.category, priority_level: v.priority_level,
        competition_class: v.competition_class,
        instagram_handle: v.instagram_handle ?? '',
      })
    },
  })

  /** Activer la collecte de Stories sur la source Instagram de la fiche. */
  const basculerStories = async (valeur: boolean) => {
    const src = d?.sources.find((x) => x.type === 'SOCIAL_REFERENCE')
    if (!src) { toast('error', 'Ajoutez d’abord un compte Instagram.'); return }
    const r = await adminMutate<{ error?: string; message?: string }>(
      'PATCH', `/api/v1/soirees/market/sources/${src.id}`, { instagram_stories_enabled: valeur })
    if (r.status === 200) {
      toast('ok', valeur ? 'Les Stories de ce compte seront collectées.' : 'Collecte des Stories désactivée.')
      await charger(); onChanged()
    } else toast('error', r.data?.message ?? 'Modification impossible.')
  }

  const majCadence = async (branch: string, seconds: number | null) => {
    if (!venueId) return
    const r = await adminMutate('PUT', `/api/v1/soirees/market/venues/${venueId}/cadence`,
      { branch, interval_s: seconds })
    if (r.status === 200) { toast('ok', 'Fréquence enregistrée.'); await charger() }
    else toast('error', 'Modification impossible.')
  }

  const basculerActif = async () => {
    if (!v) return
    const r = await adminMutate('PATCH', `/api/v1/soirees/market/venues/${v.id}`, { enabled: !v.enabled })
    if (r.status === 200) {
      toast('ok', v.enabled
        ? 'Établissement désactivé — configuration et historique conservés.'
        : 'Établissement réactivé.')
      await charger(); onChanged()
    } else toast('error', 'Action impossible.')
  }

  const archiver = async () => {
    if (!v) return
    const r = await adminMutate('POST', `/api/v1/soirees/market/venues/${v.id}/archive`, {})
    setConfirmArchive(false)
    if (r.status === 200) { toast('ok', `« ${v.name} » archivé — son historique reste consultable.`); onChanged(); onClose() }
    else toast('error', 'Archivage impossible.')
  }

  return (
    <>
      <Modal open={open} onClose={onClose} ariaLabel={v?.name ?? 'Établissement'} width={1400}
        className="vd">
        <header className="lc-modal__head vd__head">
          <div className="vd__head__id">
            <h2 className="vd__name">{v?.name ?? '…'}</h2>
            {v ? (
              <p className="vd__meta">
                {[
                  CATEGORY_OPTIONS.find((c) => c.value === v.category)?.label,
                  CLASS_OPTIONS.find((c) => c.value === v.competition_class)?.label,
                  PRIORITY_OPTIONS.find((c) => c.value === v.priority_level)?.label,
                  v.distance_km !== null ? `${v.distance_km.toLocaleString('fr-FR')} km` : null,
                  v.instagram_handle ? `@${v.instagram_handle}` : null,
                ].filter(Boolean).join(' · ')}
              </p>
            ) : null}
          </div>
          <div className="vd__head__actions">
            {v ? (
              <span className={`vd__state vd__state--${v.enabled ? 'on' : 'off'}`}>
                {v.enabled ? 'Surveillé' : 'Désactivé'}
              </span>
            ) : null}
            <button type="button" className="lc-iconbtn" onClick={onClose} aria-label="Fermer">
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="lc-modal__body vd__body">
          {!d ? <p className="lc-hint">Chargement…</p> : (
            <>
              {/* ═══ SYNCHRONISATIONS — la visibilité opérationnelle (§62) ═══ */}
              <section className="vd__sect">
                <h3 className="vd__sect__title">Collectes</h3>
                <div className="vd__branches">
                  {d.branches.map((b) => {
                    const e = ETAT_BRANCHE[b.state] ?? ETAT_BRANCHE['UNKNOWN']!
                    return (
                      <article key={b.key} className="vd__branch">
                        <div className="vd__branch__head">
                          <span className="vd__branch__name">{b.label}</span>
                          <span className={`vd__dot vd__dot--${e.ton}`} aria-hidden="true" />
                          <span className="vd__branch__state">{e.label}</span>
                        </div>
                        {b.detail ? <p className="lc-hint vd__branch__detail">{b.detail}</p> : null}
                        <dl className="vd__branch__kv">
                          <div><dt>Dernière collecte</dt>
                            <dd>{b.last_success_at ? ageFr(b.last_success_at) : 'jamais'}</dd></div>
                          <div><dt>Prochaine</dt>
                            <dd>{!b.enabled ? '—'
                              : b.interval_s === 0 ? 'manuelle uniquement'
                              : dansFr(b.next_check_at)}</dd></div>
                          <div><dt>Fréquence</dt><dd>{b.cadence}</dd></div>
                        </dl>
                        {/* §63 — une erreur se dit en français, jamais en trace technique. */}
                        {b.last_error && b.enabled ? (
                          <p className="lc-hint lc-hint--warn vd__branch__err">{b.last_error}</p>
                        ) : null}
                        {edition && canManage && b.key === 'INSTAGRAM_STORIES' ? (
                          <SwitchRow
                            checked={b.enabled}
                            onChange={(x) => void basculerStories(x)}
                            title="Collecter les Stories"
                            desc="Les Stories publiques de ce compte sont relevées, puis lues localement."
                          />
                        ) : null}
                        {edition && canManage ? (
                          <CadenceChoice branche={b} presets={presets}
                            onChange={(s) => void majCadence(b.key, s)} />
                        ) : null}
                      </article>
                    )
                  })}
                </div>
              </section>

              {/* ═══ CE QU'IL ANNONCE ═══ */}
              <VenueNights venueId={d.venue.id} />

              {/* ═══ COUVERTURE ═══ */}
              <section className="vd__sect">
                <h3 className="vd__sect__title">Couverture</h3>
                <ul className="vd__cov">
                  {Object.entries(d.coverage).map(([k, val]) => (
                    <li key={k}>
                      <span>{COVERAGE_LABEL[k] ?? k}</span>
                      <strong>{COVERAGE_FR[val] ?? val}</strong>
                    </li>
                  ))}
                </ul>
                <p className="lc-hint">
                  Une couverture honnête vaut mieux qu’une illusion d’exhaustivité :
                  les Stories collectées ne rendent pas la couverture Instagram complète.
                </p>
              </section>

              {/* ═══ ÉDITION — même surface, autre MODE ═══ */}
              {edition && canManage ? (
                <section className="vd__sect">
                  <h3 className="vd__sect__title">Informations</h3>
                  <div className="vd__grid">
                    <Field label="Catégorie">
                      <Select value={form.category} options={CATEGORY_OPTIONS} ariaLabel="Catégorie"
                        onChange={(val) => setForm((f) => ({ ...f, category: val }))} />
                    </Field>
                    <Field label="Priorité">
                      <Select value={form.priority_level} options={PRIORITY_OPTIONS} ariaLabel="Priorité"
                        onChange={(val) => setForm((f) => ({ ...f, priority_level: val }))} />
                    </Field>
                    <Field label="Type de concurrence">
                      <Select value={form.competition_class} options={CLASS_OPTIONS} ariaLabel="Type de concurrence"
                        onChange={(val) => setForm((f) => ({ ...f, competition_class: val }))} />
                    </Field>
                    <Field label="Compte Instagram"
                      helper="Sert d’identité à l’établissement, et de cible à la collecte de Stories.">
                      <TextInput value={form.instagram_handle} maxLength={60} placeholder="@compte"
                        ariaLabel="Compte Instagram"
                        onChange={(val) => setForm((f) => ({ ...f, instagram_handle: val }))} />
                    </Field>
                  </div>
                </section>
              ) : null}

              {/* Sources et recherches — même fiche, section suivante. Elles
                  vivaient dans un tiroir séparé : le contexte se perdait. */}
              {edition && canManage && detailBrut ? (
                <VenueAdvancedSections detail={detailBrut}
                  canManage={canManage} onChanged={() => { void charger(); onChanged() }} />
              ) : null}
            </>
          )}
        </div>

        {/* §105 — hiérarchie claire : primaire, secondaire, destructif à part. */}
        <footer className="lc-modal__foot vd__foot">
          <div className="vd__foot__left">
            {canManage && v ? (
              <button type="button" className="lc-btn lc-btn--ghost vd__danger"
                onClick={() => setConfirmArchive(true)}>
                <Archive size={14} aria-hidden="true" /> Archiver
              </button>
            ) : null}
          </div>
          <div className="vd__foot__right">
            {canManage && v ? (
              <button type="button" className="lc-btn" onClick={() => void basculerActif()}>
                {v.enabled
                  ? <><PauseCircle size={14} aria-hidden="true" /> Désactiver</>
                  : <><PlayCircle size={14} aria-hidden="true" /> Réactiver</>}
              </button>
            ) : null}
            {canManage && v ? (
              <button type="button" className="lc-btn" onClick={() => setScanOpen(true)}>
                <Radio size={14} aria-hidden="true" /> Scanner maintenant
              </button>
            ) : null}
            {canManage ? (
              <button type="button" className={`lc-btn${edition ? '' : ' lc-btn--primary'}`}
                onClick={() => setEdition((o) => !o)}>
                <Pencil size={14} aria-hidden="true" /> {edition ? 'Terminer l’édition' : 'Éditer'}
              </button>
            ) : null}
          </div>
        </footer>
      </Modal>

      <ConfirmDialog
        open={confirmArchive}
        title="Archiver cet établissement ?"
        confirmLabel="Archiver"
        danger
        onConfirm={() => void archiver()}
        onClose={() => setConfirmArchive(false)}
      >
        {/* §60 — les conséquences en français, jamais une clé technique. */}
        <p>
          « {v?.name ?? ''} » ne sera plus surveillé et ses collectes s’arrêtent.
          Son historique — signaux, soirées détectées — reste consultable et garde
          son nom. <strong>Rien n’est supprimé.</strong>
        </p>
      </ConfirmDialog>

      {scanOpen && venueId ? (
        <VenueScanBridge venueId={venueId} name={v?.name ?? ''} onClose={() => { setScanOpen(false); void charger() }} />
      ) : null}
    </>
  )
}

/** Les soirées annoncées — mêmes cartes que partout ailleurs (§9). */
function VenueNights({ venueId }: { venueId: string }) {
  const [d, setD] = useState<{ next: Parameters<typeof MarketEventCard>[0]['e'] | null
    upcoming: Parameters<typeof MarketEventCard>[0]['e'][] } | null>(null)
  const [capture, setCapture] = useState<string | null>(null)
  // §27 — le même panneau que partout ailleurs.
  const [sel, setSel] = useState<MarketEventDetail | null>(null)

  useEffect(() => {
    void apiGet<typeof d>(`/api/v1/soirees/market/venues/${venueId}/announcements`)
      .then(setD).catch(() => setD(null))
  }, [venueId])

  if (!d) return null
  return (
    <section className="vd__sect">
      <h3 className="vd__sect__title">Prochaine soirée détectée</h3>
      {!d.next ? (
        <p className="gd-calm">Aucune soirée annoncée à ce jour par cet établissement.</p>
      ) : (
        <>
          <MarketEventCard e={d.next} onOpen={() => setSel(d.next as MarketEventDetail)}
            onCapture={setCapture} />
          {d.upcoming.length > 0 ? (
            <>
              <h4 className="vd__sect__sub">Autres soirées annoncées</h4>
              <div className="mev-grid mev-grid--compact">
                {d.upcoming.map((e) => (
                  <MarketEventCard key={e.id} e={e} densite="compact"
                    onOpen={() => setSel(e as MarketEventDetail)} onCapture={setCapture} />
                ))}
              </div>
            </>
          ) : null}
        </>
      )}
      <MarketEventDrawer event={sel} onClose={() => setSel(null)} />

      {capture ? (
        <div className="so-capture" role="dialog" aria-label="Affiche analysée"
          onClick={() => setCapture(null)}>
          <img src={`/api/v1/market/social/stories/${capture}/capture`} alt="Affiche analysée" />
        </div>
      ) : null}
    </section>
  )
}

/** Pont vers le tiroir de scan existant — pas de second moteur de scan. */
function VenueScanBridge({ venueId, name, onClose }: {
  venueId: string; name: string; onClose: () => void
}) {
  const [Comp, setComp] = useState<null | typeof import('./VenueScanPanel.js')>(null)
  useEffect(() => { void import('./VenueScanPanel.js').then(setComp) }, [])
  if (!Comp) return null
  return <Comp.VenueScanDrawer venueId={venueId} venueName={name} open onClose={onClose} autoStart
    onFinished={undefined} />
}


/**
 * CHOIX DE CADENCE — « Manuel » et « Personnalisé » ne sont PAS la même chose.
 *
 *   MANUEL        aucune collecte automatique. Rien à régler, donc aucun
 *                 champ de durée : en afficher un laissait croire qu'on
 *                 pouvait programmer… l'absence de programmation.
 *   PERSONNALISÉ  une durée choisie par l'exploitant. C'est LÀ que le champ
 *                 a un sens, et seulement là.
 *
 * Le champ n'apparaît donc que pour « Personnalisé ».
 */
function CadenceChoice({ branche, presets, onChange }: {
  branche: BranchView
  presets: Preset[]
  onChange: (seconds: number | null) => void
}) {
  // Une valeur qui ne correspond à aucun préréglage EST une valeur
  // personnalisée : on ne la range pas dans la case la plus proche.
  const estPreset = branche.interval_s === null
    || presets.some((p) => p.seconds === branche.interval_s)
  const [mode, setMode] = useState<string>(
    estPreset ? String(branche.interval_s ?? 'auto') : 'custom')
  const [heures, setHeures] = useState<number>(
    branche.interval_s && branche.interval_s > 0 ? Math.round(branche.interval_s / 3600) : 3)

  const options = [
    ...presets.map((p) => ({ value: String(p.seconds ?? 'auto'), label: p.label })),
    { value: 'custom', label: 'Personnalisé' },
  ]

  const choisir = (v: string) => {
    setMode(v)
    if (v === 'custom') onChange(Math.max(1, heures) * 3600)
    else onChange(v === 'auto' ? null : Number(v))
  }

  return (
    <div className="vd__branch__cadence">
      <Select compact ariaLabel={`Fréquence — ${branche.label}`}
        value={mode} options={options} onChange={choisir} />

      {mode === 'custom' ? (
        <div className="vd__cadence__custom">
          <span className="lc-hint">Toutes les</span>
          <NumberInput value={heures} min={1} max={168} unit="heures"
            ariaLabel={`Intervalle en heures — ${branche.label}`}
            onChange={(raw) => {
              const h = Math.max(1, Math.min(168, Number(raw) || 1))
              setHeures(h); onChange(h * 3600)
            }} />
        </div>
      ) : null}

      {mode === '0' ? (
        <p className="lc-hint vd__branch__cost">
          Aucune collecte automatique. Vous pourrez lancer un relevé
          manuellement depuis « Scanner maintenant ».
        </p>
      ) : null}

      {branche.key === 'INSTAGRAM_STORIES' && mode !== '0' ? (
        // §25 — un rappel de coût, sans alarmisme.
        <p className="lc-hint vd__branch__cost">
          Chaque relevé de Stories est facturé : plus la fréquence est courte,
          plus la consommation augmente.
        </p>
      ) : null}
    </div>
  )
}
